import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  BadgeDollarSign,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Save,
  Search,
  UserRound,
} from "lucide-react";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

const RATE_FIELDS = [
  ["ordinary", "Ordinary"],
  ["evening", "Afternoon / Evening"],
  ["night", "Night"],
  ["saturday", "Saturday"],
  ["sunday", "Sunday"],
  ["public_holiday", "Public Holiday"],
];

export default function StaffPayrollProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [awardRateSets, setAwardRateSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [profilesResponse, awardsResponse] = await Promise.all([
        api.get("/staff-payroll-profiles"),
        api.get("/award-rate-sets"),
      ]);

      const profileRows = profilesResponse.data || [];
      const awardRows = awardsResponse.data || [];

      setProfiles(profileRows);
      setAwardRateSets(awardRows);

      const nextDrafts = {};

      for (const profile of profileRows) {
        nextDrafts[profile.staff_id] = {
          employment_type:
            profile.employment_type || "casual",
          award_rate_set_id:
            profile.award_rate_set_id || "",
          use_award_mileage:
            profile.use_award_mileage !== false,
          custom_mileage_rate:
            profile.custom_mileage_rate ?? "",
          xero_employee_id:
            profile.xero_employee_id || "",
          xero_employee_email:
            profile.xero_employee_email || "",
        };
      }

      setDrafts(nextDrafts);
    } catch (error) {
      console.error("Failed to load staff payroll profiles", error);
      toast.error(
        error?.response?.data?.detail ||
          "Failed to load staff payroll profiles"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredProfiles = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return profiles;

    return profiles.filter((profile) => {
      const searchable = [
        profile.staff_name,
        profile.email,
        profile.award_rate_set?.display_name,
        profile.xero_employee_email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(term);
    });
  }, [profiles, search]);

  const awardMap = useMemo(() => {
    return Object.fromEntries(
      awardRateSets.map((row) => [row.id, row])
    );
  }, [awardRateSets]);

  const updateDraft = (staffId, field, value) => {
    setDrafts((current) => ({
      ...current,
      [staffId]: {
        ...(current[staffId] || {}),
        [field]: value,
      },
    }));
  };

  const saveProfile = async (profile) => {
    const draft = drafts[profile.staff_id];

    if (!draft) return;

    setSavingId(profile.staff_id);

    try {
      const payload = {
        employment_type: "casual",
        award_rate_set_id:
          draft.award_rate_set_id || null,
        use_award_mileage:
          Boolean(draft.use_award_mileage),
        custom_mileage_rate:
          draft.use_award_mileage ||
          draft.custom_mileage_rate === ""
            ? null
            : Number(draft.custom_mileage_rate),
        xero_employee_id:
          draft.xero_employee_id.trim() || null,
        xero_employee_email:
          draft.xero_employee_email.trim() || null,
      };

      if (
        !draft.use_award_mileage &&
        (!Number.isFinite(payload.custom_mileage_rate) ||
          payload.custom_mileage_rate < 0)
      ) {
        toast.error("Enter a valid custom mileage rate");
        return;
      }

      await api.put(
        `/staff-payroll-profiles/${profile.staff_id}`,
        payload
      );

      toast.success(
        `${profile.staff_name} payroll profile saved`
      );

      await loadData();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Failed to save staff payroll profile"
      );
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div
      className="p-4 md:p-6 space-y-6"
      data-testid="staff-payroll-profiles-page"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <BadgeDollarSign className="h-7 w-7 text-primary" />
            <h1 className="text-2xl font-bold">
              Staff Payroll Profiles
            </h1>
          </div>

          <p className="mt-1 text-sm text-muted-foreground">
            Assign each casual staff member to the correct SCHADS
            classification and Xero employee record.
          </p>
        </div>

        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          className="inline-flex items-center justify-center rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${
              loading ? "animate-spin" : ""
            }`}
          />
          Refresh
        </button>
      </div>

      <div className="rounded-xl border bg-card p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="font-semibold">
              Current employment model: Casual
            </p>
            <p className="text-sm text-muted-foreground">
              Staff remain unassigned until a classification is
              selected and saved.
            </p>
          </div>

          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />

            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search staff"
              className="w-full rounded-md border bg-background py-2 pl-9 pr-3 text-sm"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[280px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : filteredProfiles.length === 0 ? (
        <div className="rounded-xl border bg-card py-16 text-center">
          <UserRound className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <p className="font-semibold">
            No staff payroll profiles found
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProfiles.map((profile) => {
            const draft =
              drafts[profile.staff_id] || {};
            const selectedAward =
              awardMap[draft.award_rate_set_id];
            const assigned =
              Boolean(draft.award_rate_set_id);

            return (
              <div
                key={profile.staff_id}
                className="rounded-xl border bg-card p-4 md:p-5"
                data-testid={`staff-payroll-${profile.staff_id}`}
              >
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-semibold">
                        {profile.staff_name}
                      </h2>

                      <span
                        className={
                          assigned
                            ? "rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700"
                            : "rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700"
                        }
                      >
                        {assigned
                          ? "Classification Assigned"
                          : "Unassigned"}
                      </span>
                    </div>

                    <p className="mt-1 text-sm text-muted-foreground">
                      {profile.email || "No email"}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => saveProfile(profile)}
                    disabled={savingId === profile.staff_id}
                    className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {savingId === profile.staff_id ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Save Payroll Profile
                  </button>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <Field label="Employment Type">
                    <select
                      value="casual"
                      disabled
                      className="w-full rounded-md border bg-muted px-3 py-2"
                    >
                      <option value="casual">Casual</option>
                    </select>
                  </Field>

                  <Field label="SCHADS Classification">
                    <select
                      value={draft.award_rate_set_id || ""}
                      onChange={(event) =>
                        updateDraft(
                          profile.staff_id,
                          "award_rate_set_id",
                          event.target.value
                        )
                      }
                      className="w-full rounded-md border bg-background px-3 py-2"
                    >
                      <option value="">
                        Select classification
                      </option>

                      {awardRateSets.map((award) => (
                        <option
                          key={award.id}
                          value={award.id}
                        >
                          {award.display_name}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Xero Employee ID — Required for Export">
                    <input
                      value={draft.xero_employee_id || ""}
                      onChange={(event) =>
                        updateDraft(
                          profile.staff_id,
                          "xero_employee_id",
                          event.target.value
                        )
                      }
                      placeholder="Paste Xero Employee UUID"
                      className="w-full rounded-md border bg-background px-3 py-2"
                    />
                  </Field>

                  <Field label="Xero Employee Email">
                    <input
                      type="email"
                      value={draft.xero_employee_email || ""}
                      onChange={(event) =>
                        updateDraft(
                          profile.staff_id,
                          "xero_employee_email",
                          event.target.value
                        )
                      }
                      placeholder="Employee email in Xero"
                      className="w-full rounded-md border bg-background px-3 py-2"
                    />
                  </Field>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div
                    className={
                      draft.award_rate_set_id
                        ? "rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3"
                        : "rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
                    }
                  >
                    <p className="text-sm font-semibold">
                      Award Classification
                    </p>
                    <p className="mt-1 text-sm">
                      {draft.award_rate_set_id
                        ? "Assigned"
                        : "Required before export"}
                    </p>
                  </div>

                  <div
                    className={
                      draft.xero_employee_id?.trim()
                        ? "rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3"
                        : "rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
                    }
                  >
                    <p className="text-sm font-semibold">
                      Xero Employee
                    </p>
                    <p className="mt-1 text-sm">
                      {draft.xero_employee_id?.trim()
                        ? "Employee ID recorded"
                        : "Required before export"}
                    </p>
                  </div>

                  <div className="rounded-lg border p-3">
                    <p className="text-sm font-semibold">
                      Xero export readiness
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Earnings Rate IDs are configured under
                      Award Rates.
                    </p>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border p-4">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={
                        draft.use_award_mileage !== false
                      }
                      onChange={(event) =>
                        updateDraft(
                          profile.staff_id,
                          "use_award_mileage",
                          event.target.checked
                        )
                      }
                    />

                    <span className="text-sm font-medium">
                      Use mileage allowance from selected award
                    </span>
                  </label>

                  {!draft.use_award_mileage && (
                    <div className="mt-4 max-w-xs">
                      <Field label="Custom Mileage Rate">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={
                            draft.custom_mileage_rate ?? ""
                          }
                          onChange={(event) =>
                            updateDraft(
                              profile.staff_id,
                              "custom_mileage_rate",
                              event.target.value
                            )
                          }
                          className="w-full rounded-md border bg-background px-3 py-2"
                        />
                      </Field>
                    </div>
                  )}
                </div>

                {selectedAward && (
                  <div className="mt-4 rounded-lg bg-muted p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      <p className="font-semibold">
                        {selectedAward.display_name}
                      </p>
                    </div>

                    <p className="mt-1 text-sm text-muted-foreground">
                      Effective from{" "}
                      {selectedAward.effective_from}
                    </p>

                    <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-7">
                      {RATE_FIELDS.map(([key, label]) => (
                        <div
                          key={key}
                          className="rounded-lg bg-background p-3"
                        >
                          <p className="text-xs text-muted-foreground">
                            {label}
                          </p>
                          <p className="mt-1 font-semibold">
                            {formatCurrency(
                              selectedAward.rates?.[key]
                            )}
                          </p>
                        </div>
                      ))}

                      <div className="rounded-lg bg-background p-3">
                        <p className="text-xs text-muted-foreground">
                          Mileage
                        </p>
                        <p className="mt-1 font-semibold">
                          {formatCurrency(
                            selectedAward.allowances
                              ?.mileage_per_km
                          )}
                          /km
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="space-y-2">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </label>
  );
}
